import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {ApprovalManagerService} from '../../../services/approval-manager/approval-manager.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {filter, first, takeUntil, tap} from 'rxjs/operators';
import {of, Subject} from 'rxjs';
import {ARTWORK_STATUSES} from '../../../../requests/constants/artwork-file-statuses.constants';
import {ComponentPortal} from '@angular/cdk/portal';
import {RequestActionReasonComponent} from '../../../../requests/components/request-action-reason/request-action-reason.component';
import {Overlay} from '@angular/cdk/overlay';
import {ActionsReasonService} from '../../../../requests/services/actions-reason.service';
import {UtilsService} from '../../../services/utils.service';
import {FileSizeRestriction} from '../../../models/upload-file-restrictions.model';
import {PdfViewerService} from '../../../services/pdf-viewer.service';
import {MessageService} from '../../../services/message-service.service';
import {ArtworkFile} from '../../../../requests/models/card-design-request.model';
import {Events} from '../../../../shared/services/event-service.service';
import {TranslatePipe} from '@ngx-translate/core';
import {AmMode} from '../../../../shared/models/am-mode.model';

export class ApprovalManagerConfig {
  selectedRequestItemId?: number;
  selectedArtworkFileId?: number;
  selectedVersionId?: number;
  mode: 'design' | 'approval' | 'view' | 'final-approval' = 'design';
  disableAnnotations?: boolean = false;
  approvalManagerService: ApprovalManagerService;
  fileTypesToAccept?: string[];
  restrictionOptions?: FileSizeRestriction;
}

@Component({
  selector: 'app-approval-manager',
  templateUrl: './approval-manager.component.html',
  styleUrls: ['./approval-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalManagerComponent implements OnInit, OnDestroy {

  public ARTWORK_STATUSES = ARTWORK_STATUSES;
  public approvalManagerService: ApprovalManagerService;
  private ngUnsubscribe = new Subject<void>();

  public readonly restrictionOptions: FileSizeRestriction;

  public requestName: string;
  public requestItems: Array<any>;
  private selectedArtworkFileId: number;
  selectedArtworkFileIndex: number;
  private selectedRequestItemId: number;
  private selectedVersionId: number;
  public selectedRequestItem: any;
  public selectedArtwork: any;
  public canUploadSelectedArtwork: boolean;
  public canReviewSelectedArtwork: boolean;
  public selectedVersion: any;
  public compareVersion: any;
  public readonly mode;
  public artworkFiles: Array<any>;
  public versions: Array<any>;
  public latestRejectedVersions: Array<any> = [];
  public comparisonOn = false;
  public fileTypesToAccept: string[];
  public isRestrictionSizeWarningExist = false;
  public testId = 'approval_manager';
  private disableAnnotations = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) config: ApprovalManagerConfig,
    private dialogRef: MatDialogRef<ApprovalManagerComponent>,
    private overlay: Overlay,
    private actionsReasonService: ActionsReasonService,
    private element: ElementRef,
    private cd: ChangeDetectorRef,
    private pdfViewerService: PdfViewerService,
    private messageService: MessageService,
    private translate: TranslatePipe
  ) {
    this.approvalManagerService = config.approvalManagerService;
    this.selectedRequestItemId = config.selectedRequestItemId;
    this.selectedArtworkFileId = config.selectedArtworkFileId;
    this.fileTypesToAccept = config.fileTypesToAccept;
    this.restrictionOptions = config.restrictionOptions;
    this.disableAnnotations = config.disableAnnotations || false;
    this.mode = this.disableAnnotations ? AmMode.view : config.mode;
    this.selectedVersionId = config.selectedVersionId;
    this.pdfViewerService.approvalManagerService = this.approvalManagerService;
  }

  ngOnInit(): void {
    this.approvalManagerService.onRequestNameChange()
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(requestName => {
      this.requestName = requestName;
      this.cd.markForCheck();
    });


    this.approvalManagerService.onRequestItemsChange()
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(requestItems => {
      this.requestItems = requestItems || [];
      const selectedRequestItem = this.requestItems.find(item => item.id === this.selectedRequestItemId)
        || (this.requestItems.length ? this.requestItems[0] : null);
      const artworkFiles = this.getArtworkFiles(selectedRequestItem);
      const selectedArtworkFile = artworkFiles.find(item => item.id === this.selectedArtworkFileId)
        || (artworkFiles.length ? this.artworkFiles[0] : null);
      this.setSelectedRequestItem(selectedRequestItem, false);
      this.setSelectedArtworkFile(selectedArtworkFile);
      this.cd.markForCheck();
    });
    this.pdfViewerService.showViewer('approval-manager-pdf-viewer', this.disableAnnotations);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getRequestItemLabel(item: any) {
    return this.approvalManagerService.getRequestItemLabel(item);
  }

  setSelectedRequestItem(item, propagateArtworkFile = true) {
    this.selectedRequestItem = item;
    this.artworkFiles = this.getArtworkFiles(this.selectedRequestItem);
    this.selectedRequestItemId = this.selectedRequestItem?.id || null;
    const selectedArtworkFile = this.artworkFiles.length ? this.artworkFiles[0] : null;
    if (propagateArtworkFile) {
      this.setSelectedArtworkFile(selectedArtworkFile);
    }
    this.latestRejectedVersions = [];
    this.artworkFiles?.forEach(artwork =>{
      if(artwork.versions[0].resolutions[0].status === 'REJECTED' && artwork.versions[0].resolutions[0].stageType.code === 'INT_EXT_CLIENT_PROOF'){
        this.latestRejectedVersions.push(artwork.versions[0]); }
      });
  }

  async setSelectedArtworkFile(file) {
    this.selectedArtwork = file;
    this.selectedArtworkFileId = this.selectedArtwork?.id || null;
    this.versions = this.getVersions(this.selectedArtwork);

    this.selectedArtworkFileIndex = this.artworkFiles.findIndex(item => item.id === file.id);

    let targetVersion = this.getActiveVersion(this.versions);
    if (this.selectedVersionId) {
      targetVersion = (this.versions.find(v => v.id === this.selectedVersionId));
      this.selectedVersionId = undefined;
    }
    this.setSelectedVersion(targetVersion);

    this.canUploadSelectedArtwork = await this.canUpload(this.selectedArtwork);
    this.canReviewSelectedArtwork = await this.canReview(this.selectedArtwork, this.selectedVersion);
    this.cd.markForCheck();
  }

  async setSelectedVersion(version) {
    const oldId = this.selectedVersion?.id;
    this.selectedVersion = version;
    this.canReviewSelectedArtwork = await this.canReview(this.selectedArtwork, this.selectedVersion);
    if (oldId !== version?.id) {
      this.pdfViewerService.setSelectedVersion(this.selectedVersion);
    }
    this.cd.markForCheck();
  }

  setCompareVersion(version) {
    const oldId = this.compareVersion?.id;
    this.compareVersion = version;
    if (oldId !== version?.id) {
      this.pdfViewerService.setCompareVersion(this.compareVersion);
    }
    this.cd.markForCheck();
  }

  async canReview(artworkFile: any, selectedVersion: any) {
    return artworkFile?.status === 'ACTIVE'
      && (selectedVersion.active && !selectedVersion.reviewFinalized)
      && await this.approvalManagerService.canApprove(this.mode);
  }

  async canUpload(artworkFile: any) {
    return await this.approvalManagerService.canUpload(this.mode)
      && artworkFile?.status === 'ACTIVE';
  }

  getLatestResolution(version) {
    return this.approvalManagerService.getLatestResolution(version);
  }

  getCurrentVersion(artworkFile: ArtworkFile) {
    return artworkFile.versions.find(v => v.active === true);
  }

  close(rejectedArtworks?: number): void {
    this.pdfViewerService.hideViewer();
    if (rejectedArtworks !== undefined) {
      this.dialogRef.close(rejectedArtworks > 0 ? Events.StepBack : Events.StepSubmit);
      return;
    }
    this.dialogRef.close();
  }

  review(selectedVersion: any, status: string) {
    const modalResult$ = status === ARTWORK_STATUSES.rejected ? this.openReasonModal(status === ARTWORK_STATUSES.rejected) : of(null);

    modalResult$.subscribe(async data => {
      await this.approvalManagerService
        .addVersionResolution(this.selectedRequestItem.id, this.selectedArtwork.id, selectedVersion.id, status, data?.comment);
      this.cd.markForCheck();
    });
  }

  uploadVersion(file: File) {

    if (!UtilsService.fileTypeAccepted(file, this.fileTypesToAccept)) {
      this.messageService.showSystemMessage('error',
        this.translate.transform('common.errors.file-not-supported'));
      return;
    }
    this.isRestrictionSizeWarningExist = false;
    if (UtilsService.isFileFitsBySize(file.size, this.restrictionOptions)) {
      this.approvalManagerService.uploadVersion(this.selectedRequestItem.id, this.selectedArtwork.id, file);
    } else {
      this.isRestrictionSizeWarningExist = true;
    }
  }

  toggleComparison() {
    this.comparisonOn = !this.comparisonOn;
    this.pdfViewerService.enableCompareMode(this.comparisonOn);

    const selectedVersionIndex = this.versions.indexOf(this.selectedVersion);
    if (selectedVersionIndex > 0) {
      this.setCompareVersion(this.versions[selectedVersionIndex - 1]);
    } else {
      this.setCompareVersion(this.selectedVersion);
    }

  }

  downloadSelectedVersion() {
    this.approvalManagerService.downloadVersion(this.selectedVersion, this.selectedVersion?.attachment?.fileName);
  }

  downloadAllVersions() {
    this.versions.forEach((version) => {
      this.approvalManagerService.downloadVersion(version, version.attachment?.fileName);
    });
  }

  downloadLatestRejectionsVersions() {
    this.latestRejectedVersions.forEach((version) => {
      this.approvalManagerService.downloadVersion(version, version.attachment?.fileName);
    });
  }

  downloadSelectedVersionWithAnnotations() {
    this.pdfViewerService.triggerDownloadWithAnnotations(this.selectedVersion?.attachment?.fileName);
  }

  downloadAllVersionsWithAnnotations() {
    this.pdfViewerService.triggerDownloadAllWithAnnotations(this.versions);
  }

  downloadLatestRejectionsVersionsWithAnnotations() {
    this.pdfViewerService.triggerDownloadAllWithAnnotations(this.latestRejectedVersions);
  }

  isApproval() {
    return this.mode === 'approval' || this.mode === 'final-approval';
  }

  isDesign() {
    return this.mode === 'design';
  }

  private getArtworkFiles(item: any) {
    return item ? this.approvalManagerService.getArtworkFiles(item) || [] : [];
  }

  private getVersions(artwork: any) {
    return artwork ? this.approvalManagerService.getVersions(artwork, this.mode) || [] : [];
  }

  private getActiveVersion(versions) {
    return versions.find(version => version.active);
  }

  private getActiveVersionForFile(file) {
    return this.getVersions(file).find(version => version.active);
  }

  private openReasonModal(isReject: boolean) {
    this.actionsReasonService.setAction(isReject ? 'reject' : 'step-approve');
    this.actionsReasonService.setMainButtonTitle(isReject ? 'reject' : 'step-approve');
    this.actionsReasonService.setPosition('top');
    this.actionsReasonService.setIsRequired(isReject);

    const btnId = isReject ? 'appr_mgr_reject_btn' : 'appr_mgr_approve_btn';
    const origin: HTMLButtonElement = this.element.nativeElement.querySelector(`[data-test-id="${btnId}"]`);

    const positionStrategy = this.overlay.position().flexibleConnectedTo(origin).withPositions(
      [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetX: -95, offsetY: 15 }
      ]);
    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy,
      width: 380,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const reasonPortal = new ComponentPortal(RequestActionReasonComponent);

    const ref = overlayRef.attach(reasonPortal);
    ref.instance.isRedConfirmButton = isReject;

    return this.actionsReasonService.getReason()
      .pipe(
        first(),
        tap(() => overlayRef.detach()),
        filter(data => data?.buttonClicked === 'confirm'),
      );
  }

  countFilesToApprove(artworkFiles) {
    return artworkFiles
    .map(file => this.getActiveVersionForFile(file))
    .map(activeVersion => this.getLatestResolution(activeVersion).status)
    .filter(status => status === 'UNREVIEWED')
      .length;
  }

  countRequestItems() {
    const filledRI = this.requestItems.filter(ri => this.getArtworkFiles(ri).length > 0);
    const approved = filledRI.filter(ri =>
      this.getArtworkFiles(ri)
      .map(file => this.approvalManagerService.getLatestResolution(this.getActiveVersionForFile(file)).status)
      .every(status => status === 'APPROVED'))
      .length;
    const rejectedOrApproved = filledRI.filter(ri =>
      this.getArtworkFiles(ri)
      .map(file => this.approvalManagerService.getLatestResolution(this.getActiveVersionForFile(file)).status)
      .every(status => status !== 'UNREVIEWED'))
      .length;
    return {
      inProgress: filledRI.length - rejectedOrApproved,
      rejected: rejectedOrApproved - approved,
      approved
    };

  }

  getLatestVersionComment(version) {
    const latestResolution = this.getLatestResolution(version);
    if (latestResolution.status === ARTWORK_STATUSES.rejected && !latestResolution.reviewer) {
      return 'Automatically rejected by the system';
    }
    return latestResolution.comment;
  }
}
